// Account hooks — read + update the current trainer's profile/password, plus
// the Wave 6 email-verification mutations (send link / confirm token) and the
// Wave 7 PUBLIC marketing profile (bio/handle/public toggle + asset galleries).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountApi, qk } from "../api";
import type {
  AccountUpdate,
  AssetUpdate,
  TrainerAssetKind,
  TrainerProfileUpdate,
} from "../api";

export function useAccount() {
  return useQuery({
    queryKey: qk.account.detail,
    queryFn: ({ signal }) => accountApi.get(signal),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountUpdate) => accountApi.update(body),
    onSuccess: (trainer) => {
      qc.setQueryData(qk.account.detail, trainer);
      // Profile changes (locale/display_name) also reflect in auth.me.
      void qc.invalidateQueries({ queryKey: qk.auth.me });
    },
  });
}

/**
 * Send the trainer a fresh email-verification link. No cache write — the
 * verified state only flips once the emailed link is confirmed; the caller
 * surfaces the "check your inbox" feedback from the resolved response.
 */
export function useSendEmailVerification() {
  return useMutation({
    mutationFn: () => accountApi.sendEmailVerification(),
  });
}

/**
 * Confirm a verification token (the /app/account/verify-email landing route).
 * On success the account row now carries `email_verified_at`, so refetch the
 * account so the banner/section flips to the verified state with no reload.
 */
export function useConfirmEmailVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => accountApi.confirmEmailVerification(token),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.account.detail });
    },
  });
}

// --- Wave 7: trainer identity & public marketing profile -------------------

/**
 * Invalidate every account view after a profile / asset write. Refetches the
 * profile (fields + galleries) AND the base account (the avatar + display name
 * surface in the header / dashboard), so a change reflects everywhere live.
 */
function invalidateProfile(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.account.detail });
}

/** The trainer's PUBLIC marketing profile + asset galleries. */
export function useTrainerProfile() {
  return useQuery({
    queryKey: qk.account.profile,
    queryFn: ({ signal }) => accountApi.getProfile(signal),
  });
}

/**
 * Update the PUBLIC profile (bio/background/handle/phone/public_enabled). On
 * success the freshly-resolved profile is written straight to its cache and the
 * account root invalidated. A taken handle surfaces as ApiError(409); a
 * malformed / reserved handle as ApiError(422) — the caller maps these inline.
 */
export function useUpdateTrainerProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TrainerProfileUpdate) => accountApi.updateProfile(body),
    onSuccess: (profile) => {
      qc.setQueryData(qk.account.profile, profile);
      invalidateProfile(qc);
    },
  });
}

/** Upload (and set) the public avatar — replaces the previous one server-side. */
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      accountApi.uploadAvatar(file, caption),
    onSuccess: () => invalidateProfile(qc),
  });
}

/** Append one certificate image to the gallery. */
export function useUploadCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      accountApi.uploadCertificate(file, caption),
    onSuccess: () => invalidateProfile(qc),
  });
}

/** Append one achievement image to the gallery. */
export function useUploadAchievement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      accountApi.uploadAchievement(file, caption),
    onSuccess: () => invalidateProfile(qc),
  });
}

/** Edit an owned asset's caption / order. */
export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AssetUpdate }) =>
      accountApi.updateAsset(id, body),
    onSuccess: () => invalidateProfile(qc),
  });
}

/** Delete an owned asset (+ its public file). */
export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountApi.deleteAsset(id),
    onSuccess: () => invalidateProfile(qc),
  });
}

/** Reorder a kind's gallery to the given owned-id sequence. */
export function useReorderAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      orderedIds,
    }: {
      kind: Extract<TrainerAssetKind, "certificate" | "achievement">;
      orderedIds: number[];
    }) => accountApi.reorderAssets(kind, orderedIds),
    onSuccess: () => invalidateProfile(qc),
  });
}
